<template>
    <div class="w-full">
        <DataTable :loading="loading" selectionMode="single" :value="filteredCampaignData" dataKey="advertiserName" scrollable
            :scrollHeight="'80vh'" :virtualScrollerOptions="{ itemSize: 100 }" v-model:expandedRows="expandedRows"
            v-model:selection="selectedAdvertiser"
            class="border border-gray-300 rounded-xl shadow-sm  md:col-span-1 mt-5" fluid>
            <Column style="width: 5%" expander :headerStyle="headerStyles">

            </Column>
            <Column field="advertiserName" header="Voonix advertiser name" :headerStyle="headerStyles">
            </Column>
            <template #expansion="slotProps">
                <div class="p-4">
                    <DataTable v-if="getCampaignsByAdvertiser(slotProps.data.advertiserName).value.length > 0"
                         :value="getCampaignsByAdvertiser(slotProps.data.advertiserName).value">
                        <Column field="voonixCampaignKey" header="Campaign Key" :headerStyle="nestedHeaderStyles"></Column>
                        <Column field="user.name" header="User Name" filterField="username" :headerStyle="nestedHeaderStyles">
                            <template #body="slotProps">
                                <!-- Create a router link using the data in the slotProps -->
                                <router-link :to="`/users/${slotProps.data.user.id}`" class="clickable-link">
                                    {{ slotProps.data.user.name }}
                                </router-link>
                            </template>
                        </Column>
                        <Column field="deal" header="Deal" :headerStyle="nestedHeaderStyles"></Column>
                        <Column field="siteName" header="Site name" :headerStyle="nestedHeaderStyles"></Column>
                        <Column field="affiliateLink" header="Affiliate Link" :headerStyle="nestedHeaderStyles"></Column>
                        <Column field="market" header="Market" :headerStyle="nestedHeaderStyles">
                            <template #body="{ data }">
                                <span v-tooltip.top="data.market" :class="marketClass(data.market)" class="ml-2">
                                </span>
                            </template>
                        </Column>
                        <Column :headerStyle="nestedHeaderStyles">
                            <template #body="{ data }">
                                <div class="ml-5">
                                    <Button :loading="unMappingLoading[data._id]" severity="danger"
                                        class="h-8 w-3 outline" icon="pi pi-link cursor-pointer"
                                        v-tooltip.top="'Unmap camapaign'" aria-label="Map site link"
                                        @click="openUnmapCampaignConfirmation($event, data)" />
                                </div>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script setup>
import { ref, onMounted, reactive, watch, computed } from "vue";
import { headerStyles, nestedHeaderStyles } from '@/global/headerStyles';
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import CampaignService from "@/services/CampaignService";
import SiteLinkService from "@/services/SiteLinkService";
import { marketClass } from "@/utils/markets";

const loading = ref(true);
const confirm = useConfirm();
const toast = useToast();
const campaignData = ref([]);
const selectedAdvertiser = ref(null);
const expandedRows = ref({});
const unMappingLoading = ref({});

const props = defineProps({
    searchQuery: String
});

watch(selectedAdvertiser, (newSelection) => {
    if (newSelection) {
        // Expand the selected row by setting its key in expandedRows to true
        expandedRows.value = {
            ...expandedRows.value,
            [newSelection.advertiserName]: true  // Use the dataKey ('advertiserName') as the key
        };
    }
});

const getCampaignsByAdvertiser = (advertiserName) => {
    return computed(() => {
        // Filter campaignData to find the advertiser by name and return its campaigns
        const advertiser = campaignData.value.find(ad => ad.advertiserName === advertiserName);
        return advertiser ? advertiser.campaigns : [];
    });
};

function openUnmapCampaignConfirmation(event, campaign) {
    confirm.require({
        target: event.currentTarget,
        message: `Unmapping campaign key ${campaign.voonixCampaignKey}`,
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: 'Cancel',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Unmap'
        },
        accept: () => {
            unmapCampaign(campaign);
        }
    });
}

const filteredCampaignData = computed(() => {
    if (!props.searchQuery) return campaignData.value;
    return campaignData.value.filter(campaign =>
        campaign.advertiserName?.toLowerCase().includes(props.searchQuery.toLowerCase())
        // Add more fields if you want to search by more than advertiserName
    );
});

async function unmapCampaign(campaign) {
    console.log(`Unmapping campaign id: ${campaign._id}`);
    try {
        // Step 1: Set loading state for unmapping
        unMappingLoading.value[campaign._id] = true;

        // Step 2: Call the unmap API
        const response = await SiteLinkService.unmapCampaign(campaign._id, campaign.siteBrandLinkId);

        // Step 3: Extract the advertiser name from the response
        let advName = response.data.data.advertiserName;

        // Step 4: Remove the campaign from the campaignData ref, under the correct advertiser
        campaignData.value = campaignData.value.map(advertiser => {
            if (advertiser.advertiserName === advName) {
                // Filter out the unmapped campaign from this advertiser's campaigns
                advertiser.campaigns = advertiser.campaigns.filter(c => c._id !== campaign._id);

                // If no campaigns are left for this advertiser, return null to remove the advertiser
                if (advertiser.campaigns.length === 0) {
                    return null;
                }
            }
            return advertiser;
        }).filter(advertiser => advertiser !== null); // Remove advertisers with no campaigns left

        // Step 6: Show success notification
        toast.add({ severity: 'success', summary: 'Success', detail: 'Campaign unmapped successfully', life: 3000 });

    } catch (error) {
        // Handle errors and show error notification
        console.error('Error during unmapping:', error);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to unmap campaign', life: 3000 });
    }

    // Reset the loading state after the unmapping is complete
    unMappingLoading.value[campaignData._id] = false;
}

function getMappedCampaigns() {
    CampaignService.getMapped().then((response) => {
        campaignData.value = response.data;
        console.log(campaignData.value);
    });
}

onMounted(async () => {
    await Promise.all([
        getMappedCampaigns()
    ]);
    loading.value = false;
});

</script>

<style scoped>
/* Your styles here */
</style>